import React, { useState } from 'react';
import { Instagram, Play, Heart } from 'lucide-react';
import { useInstagram } from '../utils/instagram';

/**
 * Reel Wall — 6 tiles in a 4:5 grid. Videos autoplay muted when scrolled
 * into view. Photos render statically. Feels like a live TV feed of the pass.
 *
 * Picks the 6 most-recent Reels/Videos first; falls back to photos to fill.
 */
const InstagramReelWall = () => {
  const { items, configured, loading } = useInstagram();
  const [inView, setInView] = useState(false);

  // Callback ref: fires when the DOM node attaches/detaches. This is the
  // right pattern for observing a node that only mounts AFTER data loads
  // (useEffect with [] deps would attach before the null->section swap).
  const sectionRef = React.useCallback((node) => {
    if (!node) return;
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.1 },
    );
    io.observe(node);
    // Disconnect on unmount handled by GC when node detaches; keep a
    // secondary safeguard: nulling out via effect-less pattern is fine here.
  }, []);

  if (loading || !configured || items.length === 0) return null;

  // Prefer videos, then fill with photos — up to 6 tiles in a 3-col grid
  const videos = items.filter((i) => i.media_type === 'VIDEO');
  const nonVideos = items.filter((i) => i.media_type !== 'VIDEO');
  const chosen = [...videos, ...nonVideos].slice(0, 6);

  return (
    <section
      ref={sectionRef}
      className="py-12 sm:py-16 md:py-20 bg-[#0f0f0e]"
      data-testid="instagram-reel-wall"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="mb-6 sm:mb-8 md:mb-10 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 sm:gap-3">
          <div>
            <h2
              className="text-3xl sm:text-4xl md:text-5xl text-white leading-none"
              style={{ fontFamily: 'var(--font-serif)', color: '#ffffff' }}
            >
              Watch it happen.
            </h2>
          </div>
          <a
            href="https://www.instagram.com/bricklanela"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#C9A86C] transition"
            data-testid="reel-wall-follow"
          >
            <Instagram size={16} /> More on Instagram
          </a>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
          {chosen.map((p, idx) => (
            <a
              key={p.id || idx}
              href={p.permalink}
              target="_blank"
              rel="noopener noreferrer"
              className="relative aspect-square overflow-hidden group rounded-sm block bg-black"
              data-testid={`reel-tile-${idx}`}
            >
              {p.media_type === 'VIDEO' && inView ? (
                <video
                  src={p.media_url}
                  poster={p.thumbnail_url}
                  className="w-full h-full object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img
                  src={p.thumbnail_url || p.media_url}
                  alt={p.caption || 'Brick Lane on Instagram'}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              )}
              {p.media_type === 'VIDEO' && !inView && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/20 backdrop-blur flex items-center justify-center group-hover:bg-white/40 transition-colors">
                    <Play size={22} className="text-white ml-0.5" fill="white" />
                  </div>
                </div>
              )}
              {/* Hover caption — clean tiles at rest, revealed on hover */}
              <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 bg-gradient-to-t from-black/85 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                {p.caption && (
                  <p className="text-xs md:text-sm text-white line-clamp-2 mb-1.5">
                    {p.caption}
                  </p>
                )}
                <div className="flex items-center gap-1.5 text-[10px] text-white/70 tracking-wider uppercase">
                  <Heart size={10} /> Instagram
                </div>
              </div>
            </a>
          ))}
        </div>

        <div className="md:hidden text-center mt-6">
          <a
            href="https://www.instagram.com/bricklanela"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#C9A86C]"
          >
            <Instagram size={16} /> More on Instagram
          </a>
        </div>
      </div>
    </section>
  );
};

export default InstagramReelWall;
